import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tabs,
  Tab,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SecurityIcon from '@mui/icons-material/Security';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ImageIcon from '@mui/icons-material/Image';
import { useNavigate } from 'react-router-dom';
import {
  useGetCurrentCompanyQuery,
  useUpdateCurrentCompanyMutation,
  useUpdateCompanyLogoMutation,
} from '../../../store/api/companyApi';
import { useGetRolesQuery, useGetPermissionsQuery } from '../../../store/api/roleApi';
import { usePermission } from '../../../hooks/usePermission';

const CURRENCIES = [
  'USD',
  'EUR',
  'GBP',
  'AED',
  'INR',
  'SAR',
  'MYR',
  'THB',
  'SGD',
  'AUD',
  'CAD',
  'CHF',
  'JPY',
];

const TAX_LABELS = ['VAT', 'GST', 'Tax', 'Sales Tax'];

const SALES_TEMPLATES = [
  { id: 'standard', label: 'Template 1 – Standard A4', description: 'Full invoice layout for normal A4 printing.' },
  { id: 'thermal', label: 'Template 2 – Thermal', description: 'Narrow thermal receipt style layout.' },
  { id: 'template3', label: 'Template 3', description: 'Alternative A4 layout (reserved for future use).' },
  { id: 'template4', label: 'Template 4', description: 'Compact A4 layout (reserved for future use).' },
  { id: 'template5', label: 'Template 5', description: 'Minimal invoice (reserved for future use).' },
  { id: 'template6', label: 'Template 6', description: 'Detailed invoice (reserved for future use).' },
  { id: 'template7', label: 'Template 7', description: 'Retail style layout (reserved for future use).' },
  { id: 'template8', label: 'Template 8', description: 'Service invoice layout (reserved for future use).' },
  { id: 'template9', label: 'Template 9', description: 'Landscape A4 layout (reserved for future use).' },
  { id: 'template10', label: 'Template 10', description: 'Custom layout (reserved for future use).' },
];

const defaultConfig = {
  name: '',
  email: '',
  phone: '',
  address: '',
  currency: 'USD',
  taxId: '',
  vatRate: '',
  vatNumber: '',
  taxLabel: 'VAT',
  salesVatIncluded: true,
  purchaseVatIncluded: true,
  creditDaysOptions: '7,14,30',
  maxDiscountType: '', // '' = no limit, 'percent' | 'amount'
  maxDiscountPercent: '',
  maxDiscountAmount: '',
  invoicePrefix: 'INV',
  invoiceNextNumber: '1',
  invoiceNumberPadding: '6',
  printPaperType: 'a4', // 'a4' | 'a5' | 'thermal'
  thermalPaperWidth: '80', // 58 | 80
  printItemNameFormat: 'name_sku', // name | name_sku | name_brand | name_brand_sku | brand_name | brand_name_sku
  salesPrintTemplate: 'standard',
  quotationPrintTemplate: 'standard',
  dateFormat: 'dd/MM/yyyy',
};

function Settings() {
  const [tabValue, setTabValue] = useState(0);
  const [config, setConfig] = useState(defaultConfig);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const navigate = useNavigate();

  const { data: company, isLoading: loadingCompany } = useGetCurrentCompanyQuery();
  const [updateCompany, { isLoading: saving }] = useUpdateCurrentCompanyMutation();
  const [updateCompanyLogo, { isLoading: uploadingLogo }] = useUpdateCompanyLogoMutation();
  const logoInputRef = useRef(null);
  const { data: roles } = useGetRolesQuery();
  const { data: allPermissions } = useGetPermissionsQuery();
  const { isAdmin, hasPermission: userHasPerm } = usePermission();

  useEffect(() => {
    if (company) {
      setConfig({
        name: company.name ?? '',
        email: company.email ?? '',
        phone: company.phone ?? '',
        address: company.address ?? '',
        currency: company.currency ?? 'USD',
        taxId: company.taxId ?? '',
        vatRate: company.vatRate != null ? String(company.vatRate) : '',
        vatNumber: company.vatNumber ?? '',
        taxLabel: company.taxLabel ?? 'VAT',
        salesVatIncluded: company.salesVatIncluded !== false,
        purchaseVatIncluded: company.purchaseVatIncluded !== false,
        creditDaysOptions: company.creditDaysOptions ?? '7,14,30',
        maxDiscountType: company.maxDiscountType === 'percent' || company.maxDiscountType === 'amount' ? company.maxDiscountType : '',
        maxDiscountPercent: company.maxDiscountPercent != null ? String(company.maxDiscountPercent) : '',
        maxDiscountAmount: company.maxDiscountAmount != null ? String(company.maxDiscountAmount) : '',
        invoicePrefix: company.invoicePrefix ?? 'INV',
        invoiceNextNumber: company.invoiceNextNumber != null ? String(company.invoiceNextNumber) : '1',
        invoiceNumberPadding: company.invoiceNumberPadding != null ? String(company.invoiceNumberPadding) : '6',
        printPaperType: ['a4', 'a5', 'thermal'].includes(company.printPaperType) ? company.printPaperType : 'a4',
        thermalPaperWidth: company.thermalPaperWidth != null ? String(company.thermalPaperWidth) : '80',
        printItemNameFormat: company.printItemNameFormat || 'name_sku',
        salesPrintTemplate: company.salesPrintTemplate || 'standard',
        quotationPrintTemplate: [
          'standard',
          'thermal',
          'template3',
          'template4',
          'template5',
          'template6',
          'template7',
          'template8',
          'template9',
          'template10',
        ].includes(company.quotationPrintTemplate)
          ? company.quotationPrintTemplate
          : 'standard',
        dateFormat: company.dateFormat || 'dd/MM/yyyy',
      });
    }
  }, [company]);

  const [systemSettings, setSystemSettings] = useState({
    enableNotifications: true,
    enableEmailAlerts: true,
    lowStockThreshold: 10,
  });

  const handleConfigChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    setSaveSuccess(false);
  };

  const handleSaveConfiguration = async () => {
    setSaveSuccess(false);
    try {
      await updateCompany({
        name: config.name.trim() || undefined,
        email: config.email.trim() || undefined,
        phone: config.phone.trim() || undefined,
        address: config.address.trim() || undefined,
        currency: config.currency,
        taxId: config.taxId.trim() || undefined,
        vatRate: config.vatRate === '' ? undefined : parseFloat(config.vatRate),
        vatNumber: config.vatNumber.trim() || undefined,
        taxLabel: config.taxLabel || 'VAT',
        salesVatIncluded: config.salesVatIncluded,
        purchaseVatIncluded: config.purchaseVatIncluded,
        creditDaysOptions: config.creditDaysOptions?.trim() || undefined,
        maxDiscountType:
          config.maxDiscountType === 'percent' || config.maxDiscountType === 'amount'
            ? config.maxDiscountType
            : null,
        maxDiscountPercent:
          config.maxDiscountType === 'percent' && config.maxDiscountPercent !== ''
            ? (() => {
                const n = parseFloat(config.maxDiscountPercent);
                return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
              })()
            : null,
        maxDiscountAmount:
          config.maxDiscountType === 'amount' && config.maxDiscountAmount !== ''
            ? (() => {
                const n = parseFloat(config.maxDiscountAmount);
                return Number.isFinite(n) && n >= 0 ? n : null;
              })()
            : null,
        invoicePrefix: config.invoicePrefix?.trim() || 'INV',
        invoiceNextNumber: config.invoiceNextNumber === '' ? 1 : parseInt(config.invoiceNextNumber, 10) || 1,
        invoiceNumberPadding: config.invoiceNumberPadding === '' ? 6 : parseInt(config.invoiceNumberPadding, 10) || 6,
        printPaperType: ['a4', 'a5', 'thermal'].includes(config.printPaperType) ? config.printPaperType : 'a4',
        thermalPaperWidth: config.thermalPaperWidth === '' ? 80 : parseInt(config.thermalPaperWidth, 10) || 80,
        printItemNameFormat:
          ['name', 'name_sku', 'name_brand', 'name_brand_sku', 'brand_name', 'brand_name_sku'].includes(
            config.printItemNameFormat
          )
            ? config.printItemNameFormat
            : 'name_sku',
        salesPrintTemplate:
          [
            'standard',
            'thermal',
            'template3',
            'template4',
            'template5',
            'template6',
            'template7',
            'template8',
            'template9',
            'template10',
          ].includes(config.salesPrintTemplate)
            ? config.salesPrintTemplate
            : 'standard',
        quotationPrintTemplate: [
          'standard',
          'thermal',
          'template3',
          'template4',
          'template5',
          'template6',
          'template7',
          'template8',
          'template9',
          'template10',
        ].includes(config.quotationPrintTemplate)
          ? config.quotationPrintTemplate
          : 'standard',
        dateFormat:
          ['dd/MM/yyyy', 'MM/dd/yyyy', 'yyyy-MM-dd', 'dd-MM-yyyy'].includes(config.dateFormat)
            ? config.dateFormat
            : 'dd/MM/yyyy',
      }).unwrap();
      setSaveSuccess(true);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 600 }}>
        Settings
      </Typography>

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab icon={<BusinessIcon />} label="Configuration" iconPosition="start" />
        <Tab icon={<PrintIcon />} label="Printer" iconPosition="start" />
        <Tab icon={<SettingsIcon />} label="System" iconPosition="start" />
        <Tab icon={<SecurityIcon />} label="Permissions" iconPosition="start" />
      </Tabs>

      {tabValue === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Company Configuration
            </Typography>
            {loadingCompany ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 1 }}>
                    Company Logo
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        bgcolor: 'grey.50',
                      }}
                    >
                      {company?.logoUrl ? (
                        <img
                          src={company.logoUrl}
                          alt="Company logo"
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <ImageIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                      )}
                    </Box>
                    <Box>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const formData = new FormData();
                            formData.append('logo', file);
                            await updateCompanyLogo(formData).unwrap();
                          } catch (err) {
                            alert(err?.data?.message || 'Failed to upload logo');
                          }
                          e.target.value = '';
                        }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        startIcon={<ImageIcon />}
                      >
                        {uploadingLogo ? 'Uploading...' : company?.logoUrl ? 'Change logo' : 'Set logo'}
                      </Button>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Main Company
                  </Typography>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={config.name}
                    onChange={(e) => handleConfigChange('name', e.target.value)}
                    placeholder="Your company legal name"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={config.email}
                    onChange={(e) => handleConfigChange('email', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={config.phone}
                    onChange={(e) => handleConfigChange('phone', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                    Company Address
                  </Typography>
                  <TextField
                    fullWidth
                    label="Address"
                    multiline
                    rows={3}
                    value={config.address}
                    onChange={(e) => handleConfigChange('address', e.target.value)}
                    placeholder="Street, City, State, Country, Postal Code"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                    Currency
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    label="Currency"
                    value={config.currency}
                    onChange={(e) => handleConfigChange('currency', e.target.value)}
                  >
                    {CURRENCIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }} display="flex" alignItems="center" gap={0.5}>
                    <ReceiptLongIcon fontSize="small" /> Tax Configuration (e.g. VAT)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        select
                        label="Tax label"
                        value={config.taxLabel}
                        onChange={(e) => handleConfigChange('taxLabel', e.target.value)}
                      >
                        {TAX_LABELS.map((l) => (
                          <MenuItem key={l} value={l}>
                            {l}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="VAT / Tax rate (%)"
                        value={config.vatRate}
                        onChange={(e) => handleConfigChange('vatRate', e.target.value)}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                        placeholder="e.g. 15"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        label="VAT / Tax registration number"
                        value={config.vatNumber}
                        onChange={(e) => handleConfigChange('vatNumber', e.target.value)}
                        placeholder="e.g. VAT number"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        label="Tax ID (optional)"
                        value={config.taxId}
                        onChange={(e) => handleConfigChange('taxId', e.target.value)}
                        placeholder="Business tax identifier"
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Credit sales – payment terms (days)
                  </Typography>
                  <TextField
                    fullWidth
                    label="Credit days options"
                    value={config.creditDaysOptions}
                    onChange={(e) => handleConfigChange('creditDaysOptions', e.target.value)}
                    placeholder="e.g. 7, 14, 30"
                    helperText="Comma-separated days (e.g. 7, 14, 30). Shown when creating credit sales."
                    sx={{ maxWidth: 400 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Date format
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    label="Date format"
                    value={config.dateFormat}
                    onChange={(e) => handleConfigChange('dateFormat', e.target.value)}
                    sx={{ maxWidth: 300 }}
                    helperText="Controls how dates are shown across the system."
                  >
                    <MenuItem value="dd/MM/yyyy">dd/MM/yyyy (e.g. 31/12/2026)</MenuItem>
                    <MenuItem value="MM/dd/yyyy">MM/dd/yyyy (e.g. 12/31/2026)</MenuItem>
                    <MenuItem value="yyyy-MM-dd">yyyy-MM-dd (e.g. 2026-12-31)</MenuItem>
                    <MenuItem value="dd-MM-yyyy">dd-MM-yyyy (e.g. 31-12-2026)</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Maximum discount (sales)
                  </Typography>
                  <RadioGroup
                    row
                    value={config.maxDiscountType || 'none'}
                    onChange={(e) => handleConfigChange('maxDiscountType', e.target.value === 'none' ? '' : e.target.value)}
                  >
                    <FormControlLabel value="none" control={<Radio />} label="No limit" />
                    <FormControlLabel value="percent" control={<Radio />} label="Percentage" />
                    <FormControlLabel value="amount" control={<Radio />} label="Amount per line" />
                  </RadioGroup>
                  {config.maxDiscountType === 'percent' && (
                    <TextField
                      fullWidth
                      type="number"
                      label="Maximum discount %"
                      value={config.maxDiscountPercent}
                      onChange={(e) => handleConfigChange('maxDiscountPercent', e.target.value)}
                      placeholder="e.g. 20"
                      helperText="Maximum discount % allowed per line on sales."
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      sx={{ maxWidth: 400, mt: 1 }}
                    />
                  )}
                  {config.maxDiscountType === 'amount' && (
                    <TextField
                      fullWidth
                      type="number"
                      label={`Maximum discount amount (${company?.currency || 'USD'})`}
                      value={config.maxDiscountAmount}
                      onChange={(e) => handleConfigChange('maxDiscountAmount', e.target.value)}
                      placeholder="e.g. 10"
                      helperText="Maximum discount amount allowed per line on sales."
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={{ maxWidth: 400, mt: 1 }}
                    />
                  )}
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Invoice number sequence (Sales)
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        label="Invoice prefix"
                        value={config.invoicePrefix}
                        onChange={(e) => handleConfigChange('invoicePrefix', e.target.value)}
                        placeholder="e.g. INV"
                        helperText="Used when Invoice Number is left empty."
                        sx={{ maxWidth: 300 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Next invoice number"
                        value={config.invoiceNextNumber}
                        onChange={(e) => handleConfigChange('invoiceNextNumber', e.target.value)}
                        inputProps={{ min: 1, step: 1 }}
                        helperText="Next sequence value to use."
                        sx={{ maxWidth: 300 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Zero padding"
                        value={config.invoiceNumberPadding}
                        onChange={(e) => handleConfigChange('invoiceNumberPadding', e.target.value)}
                        inputProps={{ min: 0, max: 12, step: 1 }}
                        helperText="e.g. 6 => 000001"
                        sx={{ maxWidth: 300 }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Sales &amp; Purchase VAT
                  </Typography>
                  <Grid container spacing={3} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        Sales prices
                      </Typography>
                      <RadioGroup
                        row
                        value={config.salesVatIncluded ? 'included' : 'excluded'}
                        onChange={(e) => handleConfigChange('salesVatIncluded', e.target.value === 'included')}
                      >
                        <FormControlLabel value="included" control={<Radio />} label="VAT included" />
                        <FormControlLabel value="excluded" control={<Radio />} label="VAT excluded" />
                      </RadioGroup>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Whether selling prices already include {config.taxLabel || 'VAT'} or not
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        Purchase prices
                      </Typography>
                      <RadioGroup
                        row
                        value={config.purchaseVatIncluded ? 'included' : 'excluded'}
                        onChange={(e) => handleConfigChange('purchaseVatIncluded', e.target.value === 'included')}
                      >
                        <FormControlLabel value="included" control={<Radio />} label="VAT included" />
                        <FormControlLabel value="excluded" control={<Radio />} label="VAT excluded" />
                      </RadioGroup>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Whether purchase prices already include {config.taxLabel || 'VAT'} or not
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                {saveSuccess && (
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="success" onClose={() => setSaveSuccess(false)}>
                      Configuration saved successfully.
                    </Alert>
                  </Grid>
                )}
                <Grid size={{ xs: 12 }}>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    onClick={handleSaveConfiguration}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Configuration'}
                  </Button>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {tabValue === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Printer Settings
            </Typography>
            {loadingCompany ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Printer configuration
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        select
                        label="Print format"
                        value={config.printPaperType}
                        onChange={(e) => handleConfigChange('printPaperType', e.target.value)}
                        sx={{ maxWidth: 300 }}
                      >
                        <MenuItem value="a4">A4</MenuItem>
                        <MenuItem value="a5">A5</MenuItem>
                        <MenuItem value="thermal">Thermal</MenuItem>
                      </TextField>
                    </Grid>
                    {config.printPaperType === 'thermal' && (
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          select
                          label="Thermal width"
                          value={config.thermalPaperWidth}
                          onChange={(e) => handleConfigChange('thermalPaperWidth', e.target.value)}
                          helperText="Common sizes: 58mm or 80mm"
                          sx={{ maxWidth: 300 }}
                        >
                          <MenuItem value="58">58mm</MenuItem>
                          <MenuItem value="80">80mm</MenuItem>
                        </TextField>
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                    Sales print item format
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        select
                        label="Item name format"
                        value={config.printItemNameFormat}
                        onChange={(e) => handleConfigChange('printItemNameFormat', e.target.value)}
                        sx={{ maxWidth: 360 }}
                      >
                        <MenuItem value="name">Name only</MenuItem>
                        <MenuItem value="name_sku">Name + SKU</MenuItem>
                        <MenuItem value="name_brand">Name + Brand</MenuItem>
                        <MenuItem value="name_brand_sku">Name + Brand + SKU</MenuItem>
                        <MenuItem value="brand_name">Brand + Name</MenuItem>
                        <MenuItem value="brand_name_sku">Brand + Name + SKU</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Templates
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Sales invoice templates (click to choose)
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {SALES_TEMPLATES.map((tpl) => {
                          const selected = config.salesPrintTemplate === tpl.id;
                          const isThermal = tpl.id === 'thermal';
                          return (
                            <Card
                              key={tpl.id}
                              variant={selected ? 'outlined' : 'elevation'}
                              onClick={() => handleConfigChange('salesPrintTemplate', tpl.id)}
                              sx={{
                                cursor: 'pointer',
                                borderRadius: 2,
                                borderColor: selected ? 'primary.main' : 'divider',
                                boxShadow: selected ? 4 : 1,
                                minWidth: 220,
                                maxWidth: 260,
                              }}
                            >
                              <CardContent sx={{ py: 1.5, px: 2 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: selected ? 700 : 500, mb: 0.5 }}
                                  color={selected ? 'primary.main' : 'text.primary'}
                                >
                                  {tpl.label}
                                </Typography>

                                {/* Mini visual preview */}
                                <Box
                                  sx={{
                                    mt: 1,
                                    mb: 1,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'grey.50',
                                    p: 0.5,
                                    height: 70,
                                    display: 'flex',
                                    flexDirection: isThermal ? 'column' : 'column',
                                    alignItems: 'stretch',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: 10,
                                      bgcolor: 'grey.300',
                                      borderRadius: 0.5,
                                      mb: 0.5,
                                      width: isThermal ? '70%' : '50%',
                                      mx: 'auto',
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: isThermal ? 'column' : 'row',
                                      gap: 0.4,
                                      flex: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        flex: isThermal ? 'none' : 1,
                                        height: isThermal ? 8 : '100%',
                                        bgcolor: 'grey.200',
                                        borderRadius: 0.5,
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        flex: 1,
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.3,
                                      }}
                                    >
                                      <Box sx={{ height: 6, bgcolor: 'grey.200', borderRadius: 0.5 }} />
                                      <Box sx={{ height: 6, bgcolor: 'grey.100', borderRadius: 0.5 }} />
                                      <Box sx={{ height: 6, bgcolor: 'grey.200', borderRadius: 0.5 }} />
                                    </Box>
                                  </Box>
                                </Box>

                                <Typography variant="caption" color="text.secondary">
                                  {tpl.description}
                                </Typography>
                                {selected && (
                                  <Box sx={{ mt: 1 }}>
                                    <Chip
                                      size="small"
                                      color="primary"
                                      icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                                      label="Selected"
                                    />
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                        Quotation templates (click to choose)
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {SALES_TEMPLATES.map((tpl) => {
                          const selected = config.quotationPrintTemplate === tpl.id;
                          const isThermal = tpl.id === 'thermal';
                          return (
                            <Card
                              key={`quotation-${tpl.id}`}
                              variant={selected ? 'outlined' : 'elevation'}
                              onClick={() => handleConfigChange('quotationPrintTemplate', tpl.id)}
                              sx={{
                                cursor: 'pointer',
                                borderRadius: 2,
                                borderColor: selected ? 'primary.main' : 'divider',
                                boxShadow: selected ? 4 : 1,
                                minWidth: 220,
                                maxWidth: 260,
                              }}
                            >
                              <CardContent sx={{ py: 1.5, px: 2 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: selected ? 700 : 500, mb: 0.5 }}
                                  color={selected ? 'primary.main' : 'text.primary'}
                                >
                                  {tpl.label}
                                </Typography>
                                <Box
                                  sx={{
                                    mt: 1,
                                    mb: 1,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'grey.50',
                                    p: 0.5,
                                    height: 70,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: 10,
                                      bgcolor: 'grey.300',
                                      borderRadius: 0.5,
                                      mb: 0.5,
                                      width: isThermal ? '70%' : '50%',
                                      mx: 'auto',
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: isThermal ? 'column' : 'row',
                                      gap: 0.4,
                                      flex: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        flex: isThermal ? 'none' : 1,
                                        height: isThermal ? 8 : '100%',
                                        bgcolor: 'grey.200',
                                        borderRadius: 0.5,
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        flex: 1,
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.3,
                                      }}
                                    >
                                      <Box sx={{ height: 6, bgcolor: 'grey.200', borderRadius: 0.5 }} />
                                      <Box sx={{ height: 6, bgcolor: 'grey.100', borderRadius: 0.5 }} />
                                      <Box sx={{ height: 6, bgcolor: 'grey.200', borderRadius: 0.5 }} />
                                    </Box>
                                  </Box>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {tpl.description}
                                </Typography>
                                {selected && (
                                  <Box sx={{ mt: 1 }}>
                                    <Chip
                                      size="small"
                                      color="primary"
                                      icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                                      label="Selected"
                                    />
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                {saveSuccess && (
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="success" onClose={() => setSaveSuccess(false)}>
                      Configuration saved successfully.
                    </Alert>
                  </Grid>
                )}
                <Grid size={{ xs: 12 }}>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    onClick={handleSaveConfiguration}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Printer Settings'}
                  </Button>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {tabValue === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              System Settings
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={systemSettings.enableNotifications}
                      onChange={(e) =>
                        setSystemSettings({ ...systemSettings, enableNotifications: e.target.checked })
                      }
                    />
                  }
                  label="Enable System Notifications"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={systemSettings.enableEmailAlerts}
                      onChange={(e) =>
                        setSystemSettings({ ...systemSettings, enableEmailAlerts: e.target.checked })
                      }
                    />
                  }
                  label="Enable Email Alerts"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Low Stock Threshold"
                  value={systemSettings.lowStockThreshold}
                  onChange={(e) =>
                    setSystemSettings({ ...systemSettings, lowStockThreshold: parseInt(e.target.value) || 0 })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Button variant="contained" startIcon={<SaveIcon />} disabled>
                  Save System Settings (coming soon)
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {tabValue === 3 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Roles & Permission Matrix
                </Typography>
                <Button variant="contained" size="small" onClick={() => navigate('/roles')} startIcon={<SecurityIcon />} sx={{ borderRadius: 2 }}>
                  Manage Roles
                </Button>
              </Box>
              {!roles || !allPermissions ? (
                <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
              ) : (
                <PermissionMatrix roles={roles} allPermissions={allPermissions} />
              )}
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
}

function PermissionMatrix({ roles, allPermissions }) {
  const moduleGroups = useMemo(() => {
    const groups = {};
    (allPermissions || []).forEach((p) => {
      const key = `${p.module} / ${p.resource}`;
      if (!groups[key]) groups[key] = { module: p.module, resource: p.resource, perms: {} };
      groups[key].perms[p.action] = p.id;
    });
    return groups;
  }, [allPermissions]);

  const actions = ['read', 'create', 'update', 'delete'];
  const actionLabels = { read: 'View', create: 'Create', update: 'Edit', delete: 'Delete' };

  const rolePermSets = useMemo(() => {
    const map = {};
    (roles || []).forEach((r) => {
      map[r.id] = new Set((r.permissions || []).map((p) => p.id));
    });
    return map;
  }, [roles]);

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 600 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, minWidth: 180, bgcolor: 'grey.50', position: 'sticky', left: 0, zIndex: 3 }}>Module / Resource</TableCell>
            <TableCell sx={{ fontWeight: 700, width: 80, bgcolor: 'grey.50' }}>Action</TableCell>
            {(roles || []).map((r) => (
              <TableCell key={r.id} align="center" sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 100 }}>
                {r.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(moduleGroups).map(([groupKey, group]) =>
            actions
              .filter((a) => group.perms[a])
              .map((action, idx) => (
                <TableRow key={`${groupKey}-${action}`} hover>
                  {idx === 0 && (
                    <TableCell
                      rowSpan={actions.filter((a) => group.perms[a]).length}
                      sx={{ fontWeight: 500, textTransform: 'capitalize', verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider', position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}
                    >
                      {groupKey.replace(/-/g, ' ')}
                    </TableCell>
                  )}
                  <TableCell sx={{ textTransform: 'capitalize' }}>
                    <Chip
                      label={actionLabels[action]}
                      size="small"
                      sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22, minWidth: 55 }}
                      color={action === 'read' ? 'info' : action === 'create' ? 'success' : action === 'update' ? 'warning' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                  {(roles || []).map((r) => {
                    const permId = group.perms[action];
                    const has = rolePermSets[r.id]?.has(permId);
                    return (
                      <TableCell key={r.id} align="center">
                        {has ? (
                          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        ) : (
                          <CancelIcon sx={{ color: 'grey.300', fontSize: 20 }} />
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              )),
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default Settings;
